import { memo, useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "app/components/ui/Button/Button";
import get from "lodash/get";
import findLast from "lodash/findLast";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import {
	BRANDS,
	DESTINATION_LEVELS,
	NEGOCIATED_TYPES,
	TOUR_PRODUCT_TYPES,
	ICON_SIZES,
	PRODUCT_TAG_CODES,
} from "app/constants";
import EmiratesPassNegociatedItem from "app/pages/FicheProduit/NegociatedList/EmiratesPassNegociatedItem";
import "./SmartDPProduct.scss";
import { FormattedMessage, FormattedPlural } from "react-intl";
import FlightDetailModal from "app/pages/SmartDP/Listing/FlightDetailModal/FlightDetailModal";
import List from "app/pages/.shared/List";
import { useLocation } from "react-router-dom";
import { ImagesCarousel } from "./ImagesCarousel";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import takeRight from "lodash/takeRight";
import { parse } from "qs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import SdpProductPricingBlock from "app/pages/SmartDP/Listing/SmartDPProduct/SdpProductPricingBlock/SdpProductPricingBlock";
import { shouldShowFavoriteBadge, isBrandWithAccount } from "app/utils/utils";
import { getIsDubaiProduct, getUnbeatableOffer } from "app/utils/productUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { FlightBaggageBlock } from "app/features/luggage";
import AppGlobalsContext from "app/AppGlobalsContext";
import classNames from "classnames";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { Amount } from "app/components/ui/Amount/Amount";

const IMG_SIZES_DESKTOP = {
	large: { width: 402, height: 226 },
	xlarge: { width: 402, height: 226 },
};

const getUnbeatableOfferType = ({ unbeatableOfferTag, shouldDisplayMemberOnlyTag }) => {
	if (shouldDisplayMemberOnlyTag) {
		return "membersOnly";
	}

	if (unbeatableOfferTag === PRODUCT_TAG_CODES.LIMITED_TIME) {
		return "limited";
	} else {
		return "lastDays";
	}
};

const SmartDPProduct = ({
	accommodation = {},
	onClick = () => {},
	imgSizesDesktop = IMG_SIZES_DESKTOP,
	isSpotlight,
	position, // for analytics
	isStopOver = false,
	showBadgeSecondaryText = true,
}) => {
	const { search } = useLocation();
	const queryParams = parse(search, { ignoreQueryPrefix: true });
	const [isFlightDetailOpened, toggleFlightDetail] = useState(false);
	const { track } = useAnalytics();
	const { brand, isUserConnected, strategy } = useContext(AppGlobalsContext);
	const isDubaiProduct = getIsDubaiProduct(accommodation?.geography);
	const showEmiratesPassItem = brand === BRANDS.EK && isDubaiProduct;
	const showDiscountedAccommodation = accommodation?.discountBadgeVisibility ?? false;

	const showFlightDetail = useCallback(() => {
		track("sdp-product-view-flight-detail-click");
		toggleFlightDetail(true);
	}, []);

	const hideFlightDetail = useCallback(() => {
		toggleFlightDetail(false);
	}, []);

	const flightOptions = accommodation.flightDetails?.flightOptions;

	const isBaggageFree = Boolean(
		findLast(
			flightOptions?.flightBagageOptions,
			option =>
				flightOptions?.flightBagageOption?.canBeBooked &&
				flightOptions?.flightBagageOption?.included === false &&
				option?.pricePerBooking === 0
		)
	);

	let negotiatedList = [];

	// accommodation.accommodationLabel, accommodation.boardLabel, accommodation.total are set on getSDPProducts selector
	negotiatedList.push(
		{
			label: accommodation.accommodationLabel,
			type: NEGOCIATED_TYPES.ACCOMMODATION,
		},
		{ label: accommodation.boardLabel, type: NEGOCIATED_TYPES.BOARD }
	);

	if (get(accommodation, "negotiated.length") > 0) {
		const labels = accommodation.negotiated.map((nego, index) => {
			if (index > 0) {
				return nego.label.toLowerCase();
			}
			return nego.label;
		});
		negotiatedList = negotiatedList.concat({
			label: labels.join(", "),
		});
	}

	const tripAdvisor = accommodation.tripadvisor || {};
	const holidayCheck = accommodation.holidaycheck || {};
	const accommodationName = (
		<ProductNameWithCategory category={accommodation.category} name={accommodation.name} />
	);
	const accommodationNights = accommodation?.accommodationNights || queryParams?.duration;
	const isFavorite = shouldShowFavoriteBadge(accommodation);
	const unbeatableOfferTag = getUnbeatableOffer(accommodation);
	const isTour = TOUR_PRODUCT_TYPES.includes(accommodation?.productExperience);
	const discountValue = accommodation?.discountValue;
	const discountedPercentage = accommodation?.discountPercentage;
	const totalPrice = Number(accommodation?.total) || 0;
	const publicPrice = discountValue > 0 ? totalPrice + discountValue : 0;

	const hasBadges =
		unbeatableOfferTag?.length > 0 ||
		isFavorite ||
		isTour ||
		isBaggageFree ||
		showDiscountedAccommodation;

	const handleClick = useCallback(() => {
		onClick(accommodation, position);
	}, [accommodation, onClick, position]);

	const productClass = classNames("sdp-product", {
		"sdp-product--spotlight": isSpotlight,
		"sdp-product--emirates-pass": showEmiratesPassItem,
	});

	const shouldDisplayMemberOnlyTag = isBrandWithAccount({ brand, strategy }) && !isUserConnected;

	const unbeatableOffer = getUnbeatableOfferType({
		unbeatableOfferTag,
		shouldDisplayMemberOnlyTag,
	});

	return (
		<div className={productClass} data-testid="sdp-product">
			{isSpotlight && (
				<div className="sdp-product__spotlight-title">
					<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
						<FormattedMessage id="sdp.product.spotlight.title" />
					</Typography>
				</div>
			)}
			<div className="sdp-product__wrapper">
				<div className="sdp-product__photos">
					{hasBadges && (
						<div className="sdp-product__badges">
							{unbeatableOfferTag && (
								<div className="sdp-product__badge" data-testid="sdp-product-badge">
									<BadgeItem
										type={unbeatableOffer}
										showSecondaryText={showBadgeSecondaryText}
									/>
								</div>
							)}

							{isFavorite && !unbeatableOfferTag && (
								<div className="sdp-product__badge" data-testid="sdp-product-badge">
									<BadgeItem type="topPicks" />
								</div>
							)}

							{isTour && (
								<div className="sdp-product__badge" data-testid="sdp-product-badge">
									<BadgeItem type="tours" />
								</div>
							)}
							{showDiscountedAccommodation && (
								<div className="sdp-product__badge" data-testid="sdp-product-badge">
									<BadgeItem
										type="discount"
										data-testid="discounted-accommodation-tag"
										primaryValues={{
											amount: <Amount value={discountValue} size="small" />,
										}}
									/>
								</div>
							)}
							{isBaggageFree && (
								<div className="sdp-product__badge" data-testid="sdp-product-badge">
									<BadgeItem type="luggage" />
								</div>
							)}
						</div>
					)}

					{accommodation.photos && accommodation.photos[0] && (
						<ImagesCarousel
							images={accommodation.photos}
							alt={accommodation?.name}
							imgSizesDesktop={imgSizesDesktop}
							onClick={handleClick}
						/>
					)}
				</div>
				<div className="sdp-product__body">
					<div className="sdp-product__body-container">
						<div className="sdp-product__info">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL}
								isBold
								component="div"
								data-testid="sdp-product-name"
								className="sdp-product__name"
							>
								{accommodationName}
							</Typography>

							<ProductRatingContainer
								locationName={accommodationName}
								tripAdvisor={{
									id: tripAdvisor.id,
									rating: tripAdvisor.rating,
									reviewsCount: tripAdvisor.num_reviews,
								}}
								holidayCheck={{
									...holidayCheck,
									size: "small",
								}}
							/>
							<div
								className="sdp-product__location"
								data-testid="sdp-product-location"
							>
								{takeRight(
									accommodation?.geography?.levels?.filter(
										geo => geo.label !== DESTINATION_LEVELS.OTHER
									),
									2
								)
									?.reverse()
									?.map(geo => geo.label)
									?.join(", ") || ""}
							</div>
							{accommodationNights && accommodationNights > 0 && (
								<div
									className="sdp-product__hotel-nights"
									data-testid="sdp-product-hotel-nights"
								>
									<FormattedPlural
										data-testid="night-count"
										value={accommodationNights}
										one={
											<FormattedMessage
												id="general.duration.singular"
												values={{ duration: accommodationNights }}
											>
												{accommodationNights}
											</FormattedMessage>
										}
										other={
											<FormattedMessage
												id="general.duration.plural"
												values={{ duration: accommodationNights }}
											>
												{accommodationNights}
											</FormattedMessage>
										}
									/>
								</div>
							)}

							<ul className="sdp-product__negotiated">
								{negotiatedList.map((negociated, index) => {
									return (
										<li key={index} className="sdp-product__negotiated-item">
											<NegociatedItem
												sizeIcon={ICON_SIZES.SM}
												type={negociated.type}
												label={negociated.label}
												enableLineClamp={Boolean(
													!negociated.type ||
													negociated.type === NEGOCIATED_TYPES.OTHER
												)}
												lineClampFullText={
													<div className={`sdp-product__popover-content`}>
														<List
															enableHTMLFormatting={true}
															items={get(
																accommodation,
																"negotiated",
																[]
															).map(nego => nego.label)}
														/>
													</div>
												}
											/>
										</li>
									);
								})}
								{showEmiratesPassItem && (
									<li className="sdp-product__negotiated-item">
										<EmiratesPassNegociatedItem sizeIcon={ICON_SIZES.SM} />
									</li>
								)}
							</ul>
						</div>

						<FlightBaggageBlock
							flight={accommodation.flightDetails}
							onViewDetail={!isStopOver ? showFlightDetail : undefined}
							className="sdp-product__flight-baggage"
						/>

						<FlightDetailModal
							isOpened={isFlightDetailOpened}
							close={hideFlightDetail}
							flight={accommodation.flightDetails}
							{...{ isStopOver }}
						/>
					</div>

					<div
						className={classNames("sdp-product__footer", {
							"sdp-product__footer--has-discount": !accommodation?.hideDiscountHBX,
							"sdp-product__footer--display-miles": accommodation?.displayMiles,
						})}
						data-testid="sdp-product-footer"
					>
						<SdpProductPricingBlock
							isStopOver={isStopOver}
							accommodation={accommodation}
							showDiscountedAccommodation={showDiscountedAccommodation}
							discountedPercentage={discountedPercentage}
							publicPrice={publicPrice}
							discountValue={discountValue}
							price={totalPrice}
						/>
						<Button
							variant="primary"
							onClick={handleClick}
							data-testid="sdp-product-cta"
						>
							{isStopOver ? (
								<FormattedMessage id="stop.over.product.cta.label" />
							) : (
								<FormattedMessage id="sdp.product.cta.label" />
							)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

SmartDPProduct.propTypes = {
	onClick: PropTypes.func,
	accommodation: PropTypes.object,
	imgSizesDesktop: PropTypes.object,
	position: PropTypes.number,
	isSpotlight: PropTypes.bool,
	isStopOver: PropTypes.bool,
	showBadgeSecondaryText: PropTypes.bool,
};

export default memo(SmartDPProduct);
